import LinkWrapper from "@/components/Elements/LinkWrapper";
import { Auction } from "@/types/auction";
import Link from "next/link";
import React from "react";
import ReactHtmlParser from "html-react-parser";
import AuctionCardSlider from "./AuctionCardSlider";
import { getAuctionLink } from "@/lib/links";
import NoImageFound from "@/components/NoImageFound";
import Image from "next/image";
import AuctionEndedImage from "../../AuctionEndedImage";
import moment from "moment";
import "moment/locale/sv";
import { parseToDate } from "@/lib/utils";

moment.updateLocale("sv", {
  relativeTime: {
    future: "%s",
  },
});

interface AuctionCardProps {
  auction: Auction;
  priority?: boolean;
}

const getClosingDateDays = (partAuctionStart: string, partStatus: any) => {
  const closingDate = moment(partAuctionStart);
  const currentDate = moment();

  if (!closingDate.isValid()) {
    return "Datum ej specifierat";
  }

  const dateString = closingDate.format("YYYY-MM-DD");

  // Check if the auction has ended
  if (closingDate.isBefore(currentDate, "minute")) {
    return partStatus == "ended" ? dateString : "Avslutas nu";
  }

  // Check if the auction ends tomorrow
  const isTomorrow = closingDate.isSame(
    currentDate.clone().add(1, "day"),
    "day"
  );

  if (isTomorrow) {
    return "imorgon";
  }

  // Calculate relative time difference
  const relativeTime = closingDate.from(currentDate); // e.g., "2 dagar sedan" or "om 2 dagar"

  return relativeTime;
};

const MarkerSvg = () => (
  <svg
    version="1.1"
    id="_x32_"
    xmlns="http://www.w3.org/2000/svg"
    className="h-3 w-3 md:h-3 md:w-3"
    fill="currentColor"
    viewBox="0 0 512 512"
  >
    <g>
      <path
        d="M256.016,0C158.797,0.031,80.094,78.781,80.063,175.953c0.063,14.297,3.031,28.641,7.563,43.797
c7.969,26.438,21.094,55.328,36.281,84.547c45.563,87.359,110.328,177.391,110.688,177.891L256.016,512l21.391-29.813
c0.25-0.313,37.969-52.844,76.016-116.266c19.016-31.766,38.141-66.25,52.828-98.859c7.344-16.313,13.578-32.172,18.156-47.313
c4.531-15.156,7.469-29.5,7.531-43.797C431.906,78.781,353.203,0.031,256.016,0z M373.938,204.594
c-6.344,21.156-18.25,47.906-32.594,75.359c-21.484,41.266-48.281,84.375-69.625,116.953c-5.719,8.719-10.969,16.609-15.703,23.594
c-14.891-22-35.594-53.594-55.844-87.75c-17.719-29.906-35.063-61.75-47.656-90.25c-6.297-14.188-11.391-27.547-14.781-39.094
c-3.422-11.5-5-21.281-4.953-27.453c0.016-34.109,13.75-64.734,36.078-87.156c22.391-22.328,53.016-36.063,87.156-36.094
c34.109,0.031,64.75,13.766,87.125,36.094c22.359,22.422,36.078,53.047,36.094,87.156
C379.281,182.344,377.594,192.563,373.938,204.594z"
      />
      <path
        d="M256.016,118.719c-31.594,0-57.219,25.641-57.219,57.234c0,31.609,25.625,57.219,57.219,57.219
c31.578,0,57.219-25.609,57.219-57.219C313.234,144.359,287.594,118.719,256.016,118.719z"
      />
    </g>
  </svg>
);

const AuctionCard: React.FC<AuctionCardProps> = ({ auction, priority = false }) => {
  const closingDateDays = getClosingDateDays(
    auction.partAuctionStart,
    auction.partStatus
  );

  // Use media array if available (new format with mixed sorting)
  // Otherwise fall back to separate images/videos arrays (old format)
  const hasMediaArray = auction.media && auction.media.length > 0;
  
  const cardImages = !hasMediaArray && auction.images
    ? Object.values(auction.images).map((image: any) => {
        return image;
      })
    : [];

  const cardVideos = !hasMediaArray ? auction.videos : undefined;
  const auctionLink = getAuctionLink(auction);
  const isClosed = auction.partStatus === "ended";
  const hasMedia = hasMediaArray || cardImages?.length || cardVideos?.length;
  const isNew = auction.publishedToday;

  const formatDate = (date: string) => {
    const d = parseToDate(date);

    if (isNaN(d.getTime())) {
      return "Datum ej specificerat";
    }

    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  };

  return (
    <div className="flex flex-row items-start justify-start w-full md:w-full bg-aliceblue">
      <div className="rounded-lg w-full h-full overflow-hidden flex flex-col items-start justify-start border-[1px] border-solid border-gray-light/20 relative">
        <div className="w-full rounded-t-lg bg-black/10 h-[150px] md:h-[200px] overflow-hidden relative">
          {isNew && !isClosed && (
            <div className="absolute top-0 right-0 uppercase text-slate-gray-dark bg-yellow text-[8px] md:text-xs font-semibold py-1 px-2 md:p-3 rounded-bl-lg z-[100]">
              Ny auktion
            </div>
          )}

          {hasMedia ? (
            !isClosed ? (
              <AuctionCardSlider
                media={hasMediaArray ? auction.media : undefined}
                images={!hasMediaArray ? cardImages : undefined}
                videos={!hasMediaArray ? cardVideos : undefined}
                linkTo={auctionLink}
                noZoom
                priority={priority}
              />
            ) : (
              <Link href={auctionLink}>
                {" "}
                <AuctionEndedImage
                  cover
                  image={
                    hasMediaArray 
                      ? auction.media?.find(m => m.type === 'image')?.url || ""
                      : cardImages?.[0] || ""
                  } 
                />
              </Link>
            )
          ) : (
            <Link href={auctionLink}>
              {" "}
              <NoImageFound />{" "}
            </Link>
          )}
        </div>

        <div className="w-full left-0 bg-blue/90 z-[100] text-white bottom-0">
          <div className="container flex flex-row items-center justify-between !py-2">
            <div className="uppercase font-bold text-xss">
              {formatDate(auction.partAuctionStart)}
            </div>
            <div className="flex flex-row items-center justify-start space-x-1 text-xss md:text-xs">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="relative w-3 h-3 md:w-4 md:h-4"
              >
                <mask
                  id="mask0_150_531"
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="24"
                  height="24"
                >
                  <rect width="24" height="24" fill="#ffff" />
                </mask>
                <g mask="url(#mask0_150_531)">
                  <path
                    d="M8 20H16V17C16 15.9 15.6083 14.9583 14.825 14.175C14.0417 13.3917 13.1 13 12 13C10.9 13 9.95833 13.3917 9.175 14.175C8.39167 14.9583 8 15.9 8 17V20ZM12 11C13.1 11 14.0417 10.6083 14.825 9.825C15.6083 9.04167 16 8.1 16 7V4H8V7C8 8.1 8.39167 9.04167 9.175 9.825C9.95833 10.6083 10.9 11 12 11ZM4 22V20H6V17C6 15.9833 6.2375 15.0292 6.7125 14.1375C7.1875 13.2458 7.85 12.5333 8.7 12C7.85 11.4667 7.1875 10.7542 6.7125 9.8625C6.2375 8.97083 6 8.01667 6 7V4H4V2H20V4H18V7C18 8.01667 17.7625 8.97083 17.2875 9.8625C16.8125 10.7542 16.15 11.4667 15.3 12C16.15 12.5333 16.8125 13.2458 17.2875 14.1375C17.7625 15.0292 18 15.9833 18 17V20H20V22H4Z"
                    fill="#ffffff"
                    fillOpacity="0.7"
                  />
                </g>
              </svg>

              <div className="relative text-xss md:text-xs leading-[150%] font-light flex items-center shrink-0">
                {closingDateDays}
              </div>
            </div>
          </div>
        </div>
        <div className="self-stretch bg-white flex flex-col flex-grow items-start justify-between p-4 gap-2">
          <div className="self-stretch flex flex-col items-start justify-start space-y-1 md:space-y-2">
            <Link href={auctionLink} className="!no-underline" shallow>
              <div className="self-stretch relative text-md leading-[1.2] md:text-xl font-semibold text-black font-bold">
                {isClosed && (
                  <span className="font-semibold text-sm p-2 bg-gray-50 mr-1 rounded-lg border border-aliceblue">
                    {auction.auctionId}
                  </span>
                )}{" "}
                {auction.auctionTitle}
              </div>
            </Link>

            <div className="self-stretch relative leading-1 font-normal text-xs md:text-14 flex flex-row space-x-1 items-center text-paragraph">
              <Link
                className="flex items-center !no-underline"
                href={`https://www.google.com/maps?q=${auction.partLocation}&z=14&hl=sv`}
                target="_blank"
              >
                <MarkerSvg />
                <span className="ml-2">{auction.partLocation}</span>
              </Link>
            </div>
            <div className="self-stretch relative font-normal text-xss text-paragraph leading-[150%] text-profile-gray-dark !mt-4">
              {auction?.partTitle && ReactHtmlParser(auction.partTitle)}
            </div>
          </div>
          <Link
            href={auctionLink}
            className="!no-underline text-blue !text-xss"
          >
            Se mer om auktionen
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AuctionCard;
